import React, { memo, useCallback, useState } from "react";
import {
  AHSeparator,
  AHText,
  Box,
  Circle,
  Dialog,
  EmployeeAvatar,
  ListItem,
} from "arkh";
import { graphql, useFragment, useLazyLoadQuery, useMutation } from "react-relay";
import { FlashList } from "@shopify/flash-list";
import { onPressInHaptic } from "widgets/src/onPressInHaptic";
import { TVEmployeeRoleDialogQuery } from "./__generated__/TVEmployeeRoleDialogQuery.graphql";
import { TVEmployeeRoleDialogMutation } from "./__generated__/TVEmployeeRoleDialogMutation.graphql";
import { TVEmployeeRoleDialog_EmployeeItem$key } from "./__generated__/TVEmployeeRoleDialog_EmployeeItem.graphql";
import { TVEmployeeRoleDialog_EmployeeConnection$key } from "./__generated__/TVEmployeeRoleDialog_EmployeeConnection.graphql";

type Props = {
  roleId: string;
  roleName: string;
  teamId: string;
  teamName: string;
  teamColor: string | null;
  employeeIds: string[];
  onClose: () => void;
};

export function TVEmployeeRoleDialog(props: Props) {
  const [employeeIds, setEmployeeIds] = useState([...props.employeeIds]);

  const [assignEmployeesToRole] =
    useMutation<TVEmployeeRoleDialogMutation>(graphql`
      mutation TVEmployeeRoleDialogMutation(
        $roleId: ID!
        $employeeIds: [ID!]!
      ) {
        assignEmployeesToRole(roleId: $roleId, employeeIds: $employeeIds) {
          role {
            id
            employeeIds
            employeeCount
            employees {
              id
              name
              ...Avatar_employee
            }
          }
        }
      }
    `);

  const setAssignEmployeesIds = (newEmployeeIds: string[]) => {
    setEmployeeIds(newEmployeeIds);
    assignEmployeesToRole({
      variables: {
        roleId: props.roleId,
        employeeIds: newEmployeeIds,
      },
    });
  };

  const data = useLazyLoadQuery<TVEmployeeRoleDialogQuery>(
    graphql`
      query TVEmployeeRoleDialogQuery($teamIds: [ID!]) {
        store @required(action: THROW) {
          employees(teamIds: $teamIds, sortByWorkingStatus: true) {
            ...TVEmployeeRoleDialog_EmployeeConnection
          }
        }
      }
    `,
    { teamIds: [props.teamId] },
    { fetchPolicy: "store-and-network" }
  );

  return (
    <Dialog
      visible
      onClose={props.onClose}
      height={"80%"}
      maxHeight={600}
      customTitle={
        <Box>
          <AHText type="title-small" color="text-secondary">
            {props.roleName}
          </AHText>
          <Box style={{ flexDirection: "row", alignItems: "center" }}>
            <AHText type="body" color="text-secondary" spaceRight="xsmall">
              {props.teamName}
            </AHText>
            <Circle
              size="dot"
              style={{ backgroundColor: props.teamColor ?? "#888" }}
            />
          </Box>
        </Box>
      }
    >
      <AHSeparator />
      <EmployeeList
        employeeConnection={data.store.employees}
        assignEmployeesIds={employeeIds}
        setAssignEmployeesIds={setAssignEmployeesIds}
      />
    </Dialog>
  );
}

function EmployeeList(props: {
  employeeConnection: TVEmployeeRoleDialog_EmployeeConnection$key;
  assignEmployeesIds: string[];
  setAssignEmployeesIds: (ids: string[]) => void;
}) {
  const [initialAssignEmployeesIds] = useState(props.assignEmployeesIds);

  const employeeConnection = useFragment(
    graphql`
      fragment TVEmployeeRoleDialog_EmployeeConnection on EmployeeConnection {
        edges {
          node {
            id
            name
            ...TVEmployeeRoleDialog_EmployeeItem
          }
        }
      }
    `,
    props.employeeConnection
  );

  const employees =
    employeeConnection.edges
      .map((edge) => edge.node)
      .filter((node) => !!node) ?? [];

  const sortedData = employees.slice().sort((a, b) => {
    const aIndex = initialAssignEmployeesIds.includes(a.id) ? 1 : 0;
    const bIndex = initialAssignEmployeesIds.includes(b.id) ? 1 : 0;
    return bIndex - aIndex;
  });

  type Employee = (typeof sortedData)[number];

  const renderItem = useCallback(
    ({ item }: { item: Employee }) => {
      const selected = props.assignEmployeesIds.includes(item.id);
      return (
        <EmployeeItem
          employee={item}
          selected={selected}
          onPress={(employeeId) => {
            onPressInHaptic();
            props.setAssignEmployeesIds(
              props.assignEmployeesIds.includes(employeeId)
                ? props.assignEmployeesIds.filter((id) => id !== employeeId)
                : [...props.assignEmployeesIds, employeeId]
            );
          }}
        />
      );
    },
    [props.assignEmployeesIds, props.setAssignEmployeesIds]
  );

  return (
    <Box style={{ flex: 1 }}>
      <FlashList
        data={sortedData}
        renderItem={renderItem}
        keyExtractor={(item) => item.id}
        ItemSeparatorComponent={AHSeparator}
      />
    </Box>
  );
}

const EmployeeItem = memo(function EmployeeItem(props: {
  employee: TVEmployeeRoleDialog_EmployeeItem$key;
  selected: boolean;
  onPress: (employeeId: string) => void;
}) {
  const employee = useFragment(
    graphql`
      fragment TVEmployeeRoleDialog_EmployeeItem on Employee {
        id
        name
        ...Avatar_employee
      }
    `,
    props.employee
  );

  return (
    <ListItem
      onPress={() => props.onPress(employee.id)}
      left={<EmployeeAvatar employee={employee} size="large" />}
      right={<ListItem.SelectIndicator selected={props.selected} />}
    >
      <ListItem.Title>{employee.name}</ListItem.Title>
    </ListItem>
  );
});
